import React, { useState } from "react";
import { Moon, Sun } from "lucide-react";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { SectionShell, SCard } from "./SettingsUI";
import { useTheme } from "@/contexts/ThemeContext";
import DemoModeCard from "@/components/DemoModeCard";
import api from "@/lib/api";
import { useLang, translateFr as tfr } from "@/i18n";
import { toast } from "sonner";

const LANDING = [
  { id: "/dashboard", label: "Dashboard" },
  { id: "/pipeline", label: "Pipeline" },
  { id: "/players", label: tfr("Athlètes") },
  { id: "/calendar", label: "Calendrier" },
  { id: "/contracts", label: "Contrats" },
  { id: "/performance", label: "Performance" },
];

export const SectionPreferences = ({ overview, reload }) => {
  const { theme, toggle } = useTheme();
  const { lang, setLang, t, tf } = useLang();
  const prefs = overview?.preferences || {};
  const [form, setForm] = useState({
    date_format: prefs.date_format || "DD/MM/YYYY",
    time_format: prefs.time_format || "24h",
    landing_page: prefs.landing_page || "/dashboard",
  });

  const update = async (patch) => {
    setForm((f) => ({ ...f, ...patch }));
    try {
      await api.patch("/account/preferences", patch);
      toast.success(tf("Préférence enregistrée"));
      reload?.();
    } catch (e) { toast.error(e?.response?.data?.detail || tf("Erreur")); }
  };

  return (
    <SectionShell title={tf("Préférences")} desc={tf("Personnalisez l'apparence et le comportement d'Athlynx.")} testid="section-preferences">
      <SCard title={tf("Apparence")} testid="prefs-theme-card">
        <div className="flex items-center justify-between gap-4">
          <div>
            <p className="text-sm font-medium">{tf("Thème")}</p>
            <p className="text-xs text-muted-foreground mt-0.5">{tf("Actuellement :")} {theme === "dark" ? tf("Sombre") : tf("Clair")}</p>
          </div>
          <Button variant="outline" size="sm" onClick={toggle} className="gap-2" data-testid="settings-theme-toggle">
            {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            {tf("Passer en mode")} {theme === "dark" ? tf("clair") : tf("sombre")}
          </Button>
        </div>
      </SCard>

      <SCard title="Langue / Language" testid="prefs-language-card">
        <div className="flex items-center justify-between gap-4">
          <div>
            <p className="text-sm font-medium">{t("settings.language")}</p>
            <p className="text-xs text-muted-foreground mt-0.5">{t("settings.languageDesc")}</p>
          </div>
          <Select value={lang} onValueChange={(v) => { setLang(v); update({ language: v }); }}>
            <SelectTrigger className="w-40" data-testid="prefs-language"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="fr" data-testid="pref-lang-fr">{t("settings.french")}</SelectItem>
              <SelectItem value="en" data-testid="pref-lang-en">{t("settings.english")}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </SCard>

      <SCard title={tf("Formats & navigation")} testid="prefs-formats-card">
        <div className="grid sm:grid-cols-3 gap-4">
          <div className="grid gap-2">
            <Label>{tf("Format de date")}</Label>
            <Select value={form.date_format} onValueChange={(v) => update({ date_format: v })}>
              <SelectTrigger data-testid="prefs-date-format"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="DD/MM/YYYY">{tf("JJ/MM/AAAA")}</SelectItem>
                <SelectItem value="MM/DD/YYYY">{tf("MM/JJ/AAAA")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-2">
            <Label>{tf("Format d'heure")}</Label>
            <Select value={form.time_format} onValueChange={(v) => update({ time_format: v })}>
              <SelectTrigger data-testid="prefs-time-format"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="24h">24 h</SelectItem>
                <SelectItem value="12h">12 h</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-2">
            <Label>{tf("Page d'accueil")}</Label>
            <Select value={form.landing_page} onValueChange={(v) => update({ landing_page: v })}>
              <SelectTrigger data-testid="prefs-landing-page"><SelectValue /></SelectTrigger>
              <SelectContent>{LANDING.map((l) => <SelectItem key={l.id} value={l.id}>{tf(l.label)}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
      </SCard>

      <NotifNegotiationsCard overview={overview} reload={reload} tf={tf} />
      <DemoModeCard />
    </SectionShell>
  );
};

const NotifNegotiationsCard = ({ overview, reload, tf }) => {
  const prefs = overview?.preferences?.notifications?.negotiations || { inapp: true, email: true };
  const [busy, setBusy] = useState(false);
  const setPref = async (key, value) => {
    setBusy(true);
    try {
      await api.patch("/account/preferences", { [key]: value });
      toast.success(tf("Préférences enregistrées"));
      reload?.();
    } catch { toast.error(tf("Erreur")); }
    finally { setBusy(false); }
  };
  return (
    <SCard title={tf("Négociations et offres")} desc={tf("Alertes d'échéance des offres (J-7, J-3, J-1, expiration) et envois de contre-offres.")} testid="settings-notif-negotiations">
      <div className="space-y-3">
        <div className="flex items-center justify-between gap-3">
          <div>
            <Label className="text-sm">{tf("Notifications dans l'application")}</Label>
            <p className="text-xs text-muted-foreground mt-0.5">{tf("Affichées dans la cloche de notifications.")}</p>
          </div>
          <Switch checked={!!prefs.inapp} disabled={busy} onCheckedChange={(v) => setPref("notif_negotiations_inapp", v)} data-testid="notif-nego-inapp-switch" />
        </div>
        <div className="flex items-center justify-between gap-3">
          <div>
            <Label className="text-sm">{tf("Notifications par email")}</Label>
            <p className="text-xs text-muted-foreground mt-0.5">{tf("Le suivi des échéances reste actif même si les notifications sont désactivées.")}</p>
          </div>
          <Switch checked={!!prefs.email} disabled={busy} onCheckedChange={(v) => setPref("notif_negotiations_email", v)} data-testid="notif-nego-email-switch" />
        </div>
      </div>
    </SCard>
  );
};
